import { URLS } from "../../../configuration/urls";

/**
 * Per-product configuration for subscriptions.
 *
 * Structure:
 *   signupProduct  — query param sent to the signup page (never changes per status)
 *   defaultScreen  — fallback signup screen when a pending status has no screen override
 *   nameKey        — i18n key for the product display name
 *   crossSellDescriptionKey — (optional) i18n key for the selling-point copy shown on
 *     this product's cross-sell card. Omitted for products with no cross-sell card
 *     (e.g. SPOT, which is not in UPGRADE_WIZARD_PRODUCTS).
 *   pendingStatuses — map of backend status string → status config
 *     titleKey     — i18n key for the status-specific title shown on the pending tile
 *     descriptionKey — i18n key for the helper text below the title
 *     screenOverride — (optional) overrides defaultScreen for this specific status
 *
 * To add a product: add a new top-level key.
 * To add a pending status to a product: add an entry under pendingStatuses.
 * To add a status-specific screen: set screenOverride on that status entry.
 */
export const PRODUCT_CONFIG = {
  CHECKVATID: {
    signupProduct: "checkvat",
    defaultScreen: "company",
    nameKey: "productType.CHECKVATID",
    crossSellDescriptionKey: "subscription.crossSell.description.CHECKVATID",
    pendingStatuses: {
      VALID_VATID_REQUIRED: {
        titleKey: "pendingStatus.VALID_VATID_REQUIRED.title",
        descriptionKey: "pendingStatus.VALID_VATID_REQUIRED.description"
        // no screenOverride → uses defaultScreen
      }
    }
  },
  SPOT: {
    signupProduct: "spot",
    defaultScreen: "company",
    nameKey: "productType.SPOT",
    pendingStatuses: {}
  },
  VATRULES: {
    signupProduct: "vatrules",
    defaultScreen: "company",
    nameKey: "productType.VATRULES",
    crossSellDescriptionKey: "subscription.crossSell.description.VATRULES",
    pendingStatuses: {}
  }
};

/**
 * Backend product `name` values that have an explicit localized product name.
 *
 * Keys are the technical values the backend sends, normalized: the backend is
 * not consistent about casing or spacing ("VatRules Trial", "vatrules_product",
 * "VAT  Classification  Starter"), so both the keys here and every incoming
 * name go through normalizeProductName. Values are the i18n keys holding the
 * localized name for that exact product.
 */
const PRODUCT_NAME_KEYS = {
  vatrules_product: "productName.VATRULES_PRODUCT",
  "vat classification starter": "productName.VAT Classification Starter",
  "vat classification professional":
    "productName.VAT Classification Professional",
  "vatrules trial": "productName.VatRules Trial"
};

/** i18n key for the label shown when a VATRules product has no explicit mapping. */
const DEFAULT_PRODUCT_NAME_KEY = "productName.default";

/**
 * Matches a backend name that belongs to the VATRules product family but has
 * no entry in PRODUCT_NAME_KEYS — a new plan or technical setting the backend
 * introduced before a mapping existed. Such a name gets the default label
 * instead of leaking the raw technical value into the UI.
 */
const VATRULES_FAMILY = /vatrules|vat classification/;

/**
 * Normalizes a backend name for lookup: lower-cased, outer whitespace removed,
 * and every run of inner whitespace collapsed to a single space. The backend is
 * inconsistent about both casing and spacing, and an unrecognized spacing
 * variant would otherwise downgrade a named plan to the default label.
 */
function normalizeProductName(backendName) {
  return backendName.trim().toLowerCase().replace(/\s+/g, " ");
}

/**
 * Resolves a backend-supplied subscription `name` to its localized product name.
 *
 * Backend `name` fields are raw technical strings with no i18n key attached, so
 * the whole name is mapped: "VATRULES_PRODUCT" → "VAT-classification Enterprise".
 * A VATRules name with no mapping falls back to the default label rather than
 * showing the technical value. Names of other products (CHECKVATID_PRODUCT,
 * SPOT_PRODUCT, a company name) are returned unchanged.
 *
 * Only `name` is touched — `productType` stays the API code ("VATRULES"), since
 * routing and PRODUCT_CONFIG lookups key off it.
 *
 * @param {string} backendName  display name as sent by the backend
 * @param {Object} messages  translation messages for the current language
 * @returns the localized name, the default label, or the input unchanged
 */
export function translateBackendProductName(backendName, messages) {
  if (typeof backendName !== "string") return backendName;
  const lookup = normalizeProductName(backendName);
  const nameKey = PRODUCT_NAME_KEYS[lookup];
  if (nameKey) return messages?.[nameKey] ?? backendName;
  if (VATRULES_FAMILY.test(lookup)) {
    return messages?.[DEFAULT_PRODUCT_NAME_KEY] ?? backendName;
  }
  return backendName;
}

/**
 * Returns all { productType, status } pairs that are in a pending state,
 * based on the PRODUCT_CONFIG pendingStatuses map.
 * Only products known in PRODUCT_CONFIG are considered.
 */
export function getPendingItems(productStatusInfos) {
  if (!Array.isArray(productStatusInfos)) return [];
  return productStatusInfos.reduce((acc, info) => {
    const { productType, status } = info ?? {};
    const productCfg = PRODUCT_CONFIG[productType];
    if (productCfg && status && productCfg.pendingStatuses[status]) {
      acc.push({ productType, status });
    }
    return acc;
  }, []);
}

/**
 * Builds the signup redirect URL for a product + status combination.
 * screenOverride on the status takes precedence over product defaultScreen.
 * Returns null if productType or status is not configured.
 */
export function getPendingRedirectUrl(productType, status) {
  const productCfg = PRODUCT_CONFIG[productType];
  if (!productCfg) return null;
  const statusCfg = productCfg.pendingStatuses[status];
  if (!statusCfg) return null;
  // URLS.ECLEAR_BASE_URL() returns "https://<env>.eclear-solutions.com/" with trailing slash
  const base = URLS.ECLEAR_BASE_URL().replace(/\/$/, "");
  const screen = statusCfg.screenOverride ?? productCfg.defaultScreen;
  return `${base}/signup?product=${productCfg.signupProduct}&screen=${screen}`;
}

/**
 * Returns the status config for a given productType + status.
 * Callers use titleKey / descriptionKey for i18n lookups.
 */
export function getPendingStatusConfig(productType, status) {
  return PRODUCT_CONFIG[productType]?.pendingStatuses[status] ?? null;
}

/** Product types with an in-app upgrade wizard pricing page. */
export const UPGRADE_WIZARD_PRODUCTS = ["CHECKVATID", "VATRULES"];

/**
 * Builds the in-app subscription detail route path for a productType.
 * Not gated by UPGRADE_WIZARD_PRODUCTS — every subscribed product gets a detail route.
 */
export function getSubscriptionDetailPath(productType) {
  return `/spot/manage-profile/subscriptions/${productType}`;
}

/**
 * Builds the in-app upgrade wizard route path for a productType.
 * Returns null if the product has no upgrade wizard pricing page.
 */
export function getUpgradeWizardPath(productType) {
  if (!UPGRADE_WIZARD_PRODUCTS.includes(productType)) return null;
  return `/spot/manage-profile/upgrade?product=${productType}`;
}

/**
 * Product types to recommend to a merchant as a cross-sell.
 *
 * Only products that (a) have an in-app pricing page and (b) the merchant does
 * not already own are offered — offering a product with no pricing page would
 * lead nowhere. Ownership counts any ENABLED subscription, trial included: a
 * merchant on a CheckVAT trial should be upgraded via the detail page's CTA,
 * not re-sold the same product here.
 *
 * Not gated on having a paid subscription: trial merchants — and merchants with
 * no active subscription at all — are shown the other eClear products too, and
 * pick up a payment method during the upgrade wizard's own checkout.
 */
export function getCrossSellProducts(userSubscriptions) {
  const activeSubscriptions = Array.isArray(userSubscriptions)
    ? userSubscriptions
    : [];
  const ownedProductTypes = new Set(
    activeSubscriptions
      .filter((item) => item?.status === "ENABLED")
      .map((item) => item?.productType)
  );
  return UPGRADE_WIZARD_PRODUCTS.filter(
    (productType) => !ownedProductTypes.has(productType)
  );
}
